import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  FUEL_TYPES,
  REQUIRED_VEHICLE_IMAGE_KINDS,
  TRANSMISSIONS,
  VEHICLE_CLASSES,
  VEHICLE_IMAGE_KINDS,
  minorUnitsPerMajor,
  type DriverPolicy,
  type FuelType,
  type Location,
  type Transmission,
  type Vehicle,
  type VehicleClass,
  type VehicleImageKind,
} from '@zuruflex/types';
import {
  Alert,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  FormField,
  Input,
  Textarea,
  cn,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';
import { Choice, DriverFields } from './car-fields.js';
import { PhotoSlot } from './photo-slots.js';

const THIS_YEAR = new Date().getFullYear();

/** The ones that are not required, in the order the extra slots appear. */
const OPTIONAL_IMAGE_KINDS = VEHICLE_IMAGE_KINDS.filter(
  (kind) => !REQUIRED_VEHICLE_IMAGE_KINDS.includes(kind),
);

const STEPS = ['car', 'hire', 'photos'] as const;
type Step = (typeof STEPS)[number];

/**
 * Adding a car, in three short passes rather than one long one.
 *
 * It used to be every field at once: fifteen of them and a photo grid in a
 * narrow dialog, which came to two screens of scrolling with the only
 * button at the bottom. Nothing has been dropped — a customer filters on
 * the class and the gearbox, so leaving those to be guessed later would
 * cost bookings — it is just asked three questions at a time.
 *
 * The photographs are last and no longer block the save. A partner
 * standing on a forecourt can put the car in and come back with the
 * pictures; the car lands as a draft, and both the fleet page's "Needs
 * you" filter and its own Photos step say what is still owed.
 */
export function AddCarDialog({ onClose }: { onClose: (created?: Vehicle) => void }) {
  const { t } = useTranslation(['partner', 'common']);
  const queryClient = useQueryClient();
  const { partner } = usePartner();

  const [step, setStep] = useState<Step>('car');

  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState(String(THIS_YEAR));
  const [plateNumber, setPlateNumber] = useState('');
  const [vehicleClass, setVehicleClass] = useState<VehicleClass>('SEDAN');
  const [transmission, setTransmission] = useState<Transmission>('AUTOMATIC');
  const [fuelType, setFuelType] = useState<FuelType>('PETROL');
  const [seats, setSeats] = useState('5');
  const [doors, setDoors] = useState('4');
  const [locationId, setLocationId] = useState('');
  const [dailyRate, setDailyRate] = useState('');
  const [driverPolicy, setDriverPolicy] = useState<DriverPolicy>('SELF_DRIVE_ONLY');
  const [driverRate, setDriverRate] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<Partial<Record<VehicleImageKind, File>>>({});
  const [showOptional, setShowOptional] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The car is saved before its photos are uploaded, so a failure part way
   * through leaves a real vehicle behind. Holding its id means a retry
   * resumes the upload instead of trying to create the car again — which
   * would fail on the duplicate plate and strand the first one for good.
   */
  const [savedId, setSavedId] = useState<string | null>(null);

  /**
   * Previews for files that have not been uploaded yet. Created together and
   * revoked together, so swapping a photo a few times does not leave the
   * page holding blobs nothing points at.
   */
  const previews = useMemo(() => {
    const entries = Object.entries(photos).filter(([, file]) => file) as [VehicleImageKind, File][];
    return Object.fromEntries(
      entries.map(([kind, file]) => [kind, URL.createObjectURL(file)]),
    ) as Partial<Record<VehicleImageKind, string>>;
  }, [photos]);

  useEffect(
    () => () => {
      for (const url of Object.values(previews)) URL.revokeObjectURL(url);
    },
    [previews],
  );

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.listLocations(),
  });

  // The branch decides the currency, so the rate field can say which one
  // the number will be read as before anything is saved. Until one is
  // picked it falls back to the partner's own currency — a Kigali owner
  // was being shown a cedi rate field on a form about their own car.
  const chosen = useMemo<Location | undefined>(
    () => locations?.find((location) => location.id === locationId),
    [locations, locationId],
  );
  const currency = chosen?.currency ?? partner.currency;
  const locationOptions = useMemo(
    () =>
      (locations ?? []).map((location) => ({
        value: location.id,
        label: `${location.name} — ${location.city}`,
      })),
    [locations],
  );

  /**
   * The partner's default driver rate, offered as a starting value so a
   * fleet is not asked the same number thirty times.
   *
   * Only where the currencies match: a partner is paid in their own
   * currency but a car is priced in its branch's, so a cedi default on a
   * Kigali car would be the wrong number rather than a helpful one.
   */
  const inheritable =
    partner.providesDrivers && partner.currency === currency ? partner.driverDailyRate : null;

  useEffect(() => {
    if (driverPolicy === 'SELF_DRIVE_ONLY' || driverRate || inheritable == null) return;
    setDriverRate(String(inheritable / minorUnitsPerMajor(currency)));
  }, [driverPolicy, driverRate, inheritable, currency]);

  const create = useMutation({
    mutationFn: async () => {
      /**
       * Rates are typed in whole cedis and stored in pesewas — and in
       * whole francs in Kigali, where there is no minor unit at all. The
       * per-currency factor is the only correct way to convert.
       */
      const rate = Math.round(Number(dailyRate) * minorUnitsPerMajor(currency));

      const vehicle = savedId
        ? await api.getVehicle(savedId)
        : await api.createVehicle({
            make: make.trim(),
            model: model.trim(),
            year: Number(year),
            plateNumber: plateNumber.trim().toUpperCase(),
            vehicleClass,
            transmission,
            fuelType,
            seats: Number(seats),
            doors: Number(doors),
            locationId,
            dailyRate: rate,
            driverPolicy,
            ...(driverPolicy === 'SELF_DRIVE_ONLY'
              ? {}
              : { driverDailyRate: Math.round(Number(driverRate) * minorUnitsPerMajor(currency)) }),
            description: description.trim() || undefined,
          });
      setSavedId(vehicle.id);

      /**
       * Sequential rather than parallel, and skipping what a failed first
       * attempt already got through: the retry resumes rather than
       * uploading somebody's whole gallery twice.
       */
      const already = new Set(vehicle.images.map((image) => image.kind));
      for (const kind of VEHICLE_IMAGE_KINDS) {
        const photo = photos[kind];
        if (!photo || already.has(kind)) continue;
        await api.uploadVehicleImage(vehicle.id, photo, { kind });
      }

      return vehicle;
    },
    onMutate: () => setError(null),
    onError: (cause) =>
      setError(
        [
          cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'),
          // Otherwise a second failure reads as though nothing was saved,
          // and they go looking for a car that is already in their fleet.
          savedId ? t('addCar.savedRetryPhotos') : null,
        ]
          .filter(Boolean)
          .join(' '),
      ),
    onSuccess: async (vehicle) => {
      await queryClient.invalidateQueries({ queryKey: ['my-fleet'] });
      onClose(vehicle);
    },
  });

  /*
   * A car let with a driver cannot be quoted without knowing what the
   * driver costs, and the publishing gate refuses one. Asked for here so
   * the dialog stops rather than the API.
   */
  const driverRateNeeded = driverPolicy !== 'SELF_DRIVE_ONLY' && !(Number(driverRate) > 0);

  /**
   * What each step still owes, so Next can refuse rather than the last
   * button in the dialog refusing on behalf of a field three screens up.
   */
  const incomplete: Record<Step, boolean> = {
    car: !(make.trim() && model.trim() && plateNumber.trim() && Number(year) > 0),
    hire: !(locationId && Number(dailyRate) > 0 && !driverRateNeeded),
    photos: false,
  };

  /**
   * A draft is allowed to be incomplete — somebody adding a car from the
   * forecourt may only have the outside of it on their phone — so the
   * shortfall is named rather than used to disable the button. Publishing
   * is where the set is actually required, and the fleet page and the
   * car's own page both say what is left.
   */
  const missingPhotos = REQUIRED_VEHICLE_IMAGE_KINDS.filter((kind) => !photos[kind]);

  const at = STEPS.indexOf(step);
  const previous = STEPS[at - 1];
  const next = STEPS[at + 1];
  const blocked = incomplete[step];

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      {/*
        Near full screen, because this is a task rather than a question. The
        old centred box was 512px wide with fifteen fields in it; at this
        size a step is a screen of its own with room to breathe, and the
        fields stay in a readable column rather than stretching to 1200px.
      */}
      <DialogContent
        className={cn(
          'flex h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-6xl flex-col gap-0 p-0',
          'overflow-hidden sm:max-w-6xl md:flex-row',
        )}
      >
        {/*
          The steps as a rail, in the same black the app's own sidebar uses
          — a wizard you are three screens into should still look like part
          of the product. It carries `dark` itself, so everything nested
          resolves against dark tokens whatever the page is set to.
        */}
        <aside className="dark hidden w-64 shrink-0 flex-col bg-ink-950 p-4 md:flex">
          <p
            aria-hidden
            className="px-3 pb-5 pt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted"
          >
            {t('addCar.title')}
          </p>

          <ol className="flex flex-col gap-1">
            {STEPS.map((key, index) => {
              const done = index < at;
              const here = key === step;
              return (
                <li key={key}>
                  <button
                    type="button"
                    // Forward only once the steps in between would let you
                    // through anyway; back is always allowed.
                    disabled={index > at}
                    onClick={() => setStep(key)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-field px-3 py-2.5 text-left transition-colors',
                      here
                        ? 'bg-surface-hover'
                        : done
                          ? 'hover:bg-surface-hover'
                          : 'cursor-default',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                        here
                          ? 'bg-accent text-accent-text'
                          : done
                            ? 'bg-success/20 text-success'
                            : 'bg-surface-hover text-text-muted',
                      )}
                    >
                      {done ? <Check className="size-3" /> : index + 1}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={cn(
                          'block text-sm',
                          here ? 'font-semibold text-text' : done ? 'text-text' : 'text-text-muted',
                        )}
                      >
                        {t(`addCar.step.${key}`)}
                      </span>
                      <span className="mt-0.5 block text-xs leading-snug text-text-muted">
                        {t(`addCar.step.${key}Hint`)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="shrink-0 border-b border-border-default px-5 py-4 md:px-8 md:py-5">
            {/* On a phone the rail is gone, so the header carries where you
                are. On a desktop the rail already says it. */}
            <p className="text-xs font-medium text-text-muted md:hidden">
              {t('addCar.stepOf', { step: at + 1, total: STEPS.length })}
            </p>
            <DialogTitle className="mt-0.5 md:mt-0">{t(`addCar.step.${step}`)}</DialogTitle>
            <DialogDescription className="mt-1">{t(`addCar.step.${step}Hint`)}</DialogDescription>
          </div>

          <div className="flex min-h-0 flex-1 overflow-y-auto px-5 py-6 md:px-8">
            {/*
              Capped rather than full width. A name field spanning a
              1200px dialog is harder to read, not easier; the photo grid
              is the one thing that earns the extra room.

              `m-auto` rather than `justify-center`, so a short step sits in
              the middle of the panel and a tall one still scrolls from its
              own top instead of having it clipped off.
            */}
            <div className={cn('m-auto w-full', step === 'photos' ? 'max-w-4xl' : 'max-w-2xl')}>
              {error ? (
                <Alert variant="danger" className="mb-5">
                  {error}
                </Alert>
              ) : null}

              {step === 'car' ? (
                <div className="flex flex-col gap-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField label={t('addCar.make')} required>
                      {(field) => (
                        <Input
                          {...field}
                          value={make}
                          required
                          autoFocus
                          placeholder="Toyota"
                          onChange={(event) => setMake(event.target.value)}
                        />
                      )}
                    </FormField>
                    <FormField label={t('addCar.model')} required>
                      {(field) => (
                        <Input
                          {...field}
                          value={model}
                          required
                          placeholder="Corolla"
                          onChange={(event) => setModel(event.target.value)}
                        />
                      )}
                    </FormField>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField label={t('addCar.year')} required>
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          min={1980}
                          max={THIS_YEAR + 1}
                          value={year}
                          required
                          onChange={(event) => setYear(event.target.value)}
                        />
                      )}
                    </FormField>
                    <FormField label={t('common:field.plate')} required>
                      {(field) => (
                        <Input
                          {...field}
                          value={plateNumber}
                          required
                          placeholder="GR-2401-23"
                          className="font-mono uppercase"
                          onChange={(event) => setPlateNumber(event.target.value)}
                        />
                      )}
                    </FormField>
                  </div>

                  <Choice
                    label={t('common:field.class')}
                    options={VEHICLE_CLASSES}
                    value={vehicleClass}
                    onChange={setVehicleClass}
                    render={(option) => t(`common:vehicleClass.${option}`)}
                  />

                  <div className="grid gap-5 sm:grid-cols-2">
                    <Choice
                      label={t('common:field.transmission')}
                      options={TRANSMISSIONS}
                      value={transmission}
                      onChange={setTransmission}
                      render={(option) => t(`common:transmission.${option}`)}
                    />
                    <Choice
                      label={t('common:field.fuel')}
                      options={FUEL_TYPES}
                      value={fuelType}
                      onChange={setFuelType}
                      render={(option) => t(`common:fuelType.${option}`)}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField label={t('common:field.seats')} required>
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          min={1}
                          max={60}
                          value={seats}
                          required
                          onChange={(event) => setSeats(event.target.value)}
                        />
                      )}
                    </FormField>
                    <FormField label={t('common:field.doors')} required>
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          min={1}
                          max={8}
                          value={doors}
                          required
                          onChange={(event) => setDoors(event.target.value)}
                        />
                      )}
                    </FormField>
                  </div>
                </div>
              ) : null}

              {step === 'hire' ? (
                <div className="flex flex-col gap-5">
                  <FormField label={t('addCar.pickUpPoint')} hint={t('addCar.pickUpHint')} required>
                    {(field) => (
                      <Combobox
                        id={field.id}
                        options={locationOptions}
                        value={locationId}
                        onChange={setLocationId}
                        placeholder={t('addCar.choosePickUp')}
                        searchPlaceholder={t('addCar.searchPickUp')}
                        emptyMessage={t('addCar.noPickUp')}
                      />
                    )}
                  </FormField>

                  <DriverFields
                    available={partner.providesDrivers}
                    currency={currency}
                    policy={driverPolicy}
                    onPolicyChange={setDriverPolicy}
                    rate={driverRate}
                    onRateChange={setDriverRate}
                  />

                  <FormField
                    label={t('addCar.dailyRate', { currency })}
                    hint={t('addCar.rateHint')}
                    required
                  >
                    {(field) => (
                      <Input
                        {...field}
                        type="number"
                        min={1}
                        step="1"
                        value={dailyRate}
                        required
                        placeholder="450"
                        onChange={(event) => setDailyRate(event.target.value)}
                      />
                    )}
                  </FormField>

                  <FormField label={t('addCar.description')}>
                    {(field) => (
                      <Textarea
                        {...field}
                        rows={4}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                      />
                    )}
                  </FormField>
                </div>
              ) : null}

              {step === 'photos' ? (
                <div className="flex flex-col gap-5">
                  {/*
                    Named slots rather than a pile of thumbnails. A partner
                    asked for "photos" sends three shots of the same front
                    wing; asked for the dashboard and the boot, they send
                    the pictures customers write in to ask for.
                  */}
                  <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                    {REQUIRED_VEHICLE_IMAGE_KINDS.map((kind) => (
                      <PhotoSlot
                        key={kind}
                        kind={kind}
                        previewUrl={previews[kind]}
                        disabled={create.isPending}
                        onPick={(file) => setPhotos({ ...photos, [kind]: file })}
                        onRemove={() => setPhotos({ ...photos, [kind]: undefined })}
                      />
                    ))}
                  </div>

                  <div>
                    <button
                      type="button"
                      aria-expanded={showOptional}
                      onClick={() => setShowOptional(!showOptional)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-text hover:text-accent"
                    >
                      <ChevronDown
                        className={cn('size-4 transition-transform', showOptional && 'rotate-180')}
                      />
                      {t('addCar.photosMore')}
                    </button>

                    {showOptional ? (
                      <>
                        <p className="mb-4 mt-1.5 text-xs text-text-muted">
                          {t('addCar.photosMoreHint')}
                        </p>
                        <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                          {OPTIONAL_IMAGE_KINDS.map((kind) => (
                            <PhotoSlot
                              key={kind}
                              kind={kind}
                              previewUrl={previews[kind]}
                              disabled={create.isPending}
                              onPick={(file) => setPhotos({ ...photos, [kind]: file })}
                              onRemove={() => setPhotos({ ...photos, [kind]: undefined })}
                            />
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/* What is owed before it can go live, said as a fact
                      rather than enforced here: this saves a draft. */}
                  {missingPhotos.length > 0 ? (
                    <Alert variant="info">
                      {t('addCar.photosStillNeeded', {
                        kinds: missingPhotos
                          .map((kind) => t(`common:vehicleImageKind.${kind}`).toLocaleLowerCase())
                          .join(', '),
                      })}
                    </Alert>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {/* Pinned, so the way on is in the same place on every step and
              never at the bottom of a scroll. */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border-default px-5 py-4 md:px-8">
            {previous ? (
              <Button variant="ghost" onClick={() => setStep(previous)}>
                <ChevronLeft className="size-4" />
                {t(`addCar.step.${previous}`)}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => onClose()}>
                {t('common:action.cancel')}
              </Button>
            )}

            {next ? (
              <Button disabled={blocked} onClick={() => setStep(next)}>
                {t(`addCar.step.${next}`)}
                <ChevronRight className="size-4" />
              </Button>
            ) : (
              <Button
                disabled={incomplete.car || incomplete.hire}
                isLoading={create.isPending}
                onClick={() => create.mutate()}
              >
                {savedId ? t('addCar.retry') : t('addCar.submit')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
